import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { fetchApi } from '@/api/http'
import { getTenantEntryPath } from '@/config/tenant'
import { SOLONLINE_SITE_URL, WA_APP, WA_ESPACO } from '@/config/sales'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import styles from './AppHubPage.module.css'

type Espaco = { slug: string; nome: string; logoUrl: string | null }

const STEPS = [
  {
    n: '01',
    title: 'Encontre o espaço',
    text: 'Pesquise pelo nome ou abra o link que o salão, a clínica ou a barbearia lhe enviou.',
  },
  {
    n: '02',
    title: 'Escolha serviço e hora',
    text: 'Vê preços, duração e horários reais da equipa — sem criar conta numa app de terceiros.',
  },
  {
    n: '03',
    title: 'Receba o lembrete',
    text: 'A marcação fica no seu histórico e o espaço é avisado. Pode voltar sempre pelo mesmo sítio.',
  },
] as const

const DETAILS = [
  {
    title: 'Um sítio para todos os espaços',
    text: 'A app GoBooking é genérica de propósito: qualquer cliente encontra o seu salão, clínica ou estúdio e entra na marca dele — não numa lista de concorrentes.',
  },
  {
    title: 'Cada negócio continua exclusivo',
    text: 'Depois de entrar, só vê aquele espaço: serviços, equipa, unidades e marca. O directório serve para chegar lá, não para misturar agendas.',
  },
  {
    title: 'Tarefas para quem gere',
    text: 'No plano Completo, a equipa organiza o dia com tarefas dentro do GoBooking — para além da agenda de marcações. É uma vantagem que um directório de marcações não traz.',
  },
  {
    title: 'No telemóvel ou no browser',
    text: 'Não é obrigatório instalar nada. Quem marca muitas vezes pode guardar o espaço. O negócio, se quiser, publica ainda uma app só com o seu nome.',
  },
] as const

export function AppHubPage() {
  const navigate = useNavigate()
  const [espacos, setEspacos] = useState<Espaco[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [slug, setSlug] = useState('')

  useEffect(() => {
    document.title = 'App GoBooking — marcar em qualquer espaço'
    let cancelled = false
    fetchApi('/api/public/espacos')
      .then(async (res) => {
        if (!res.ok) throw new Error('lista')
        const data = (await res.json()) as { espacos?: Espaco[] }
        if (!cancelled) setEspacos(Array.isArray(data.espacos) ? data.espacos : [])
      })
      .catch(() => {
        if (!cancelled) setErro('Não foi possível carregar os espaços neste momento.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtrados = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return espacos
    return espacos.filter(
      (e) => e.nome.toLowerCase().includes(term) || e.slug.toLowerCase().includes(term)
    )
  }, [espacos, q])

  const abrirSlug = (event: FormEvent) => {
    event.preventDefault()
    const clean = slug.trim().toLowerCase().replace(/\s+/g, '-')
    if (clean.length < 2) return
    navigate(getTenantEntryPath(clean))
  }

  return (
    <div className={styles.page}>
      <header className={styles.nav}>
        <Link to="/" className={styles.brand}>
          GoBooking
        </Link>
        <nav className={styles.navLinks}>
          <a href="#espacos">Espaços</a>
          <a href="#como">Como funciona</a>
          <Link to="/#precos">Para o seu negócio</Link>
        </nav>
      </header>

      <section className={styles.hero}>
        <p className={styles.kicker}>App para clientes</p>
        <h1>Marque no seu espaço, sem se perder num directório.</h1>
        <p className={styles.lead}>
          Uma aplicação para todos os clientes GoBooking. Escolhe o salão, a clínica ou a barbearia e entra
          num ambiente só dessa marca — serviços, preços, duração e horários reais.
        </p>
        <form className={styles.search} onSubmit={(e) => e.preventDefault()} role="search">
          <label className={styles.sr} htmlFor="q">
            Pesquisar espaço
          </label>
          <input
            id="q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nome do espaço…"
            autoComplete="off"
          />
        </form>
      </section>

      <section id="espacos" className={styles.section}>
        <div className={styles.sectionHead}>
          <h2>Espaços na plataforma</h2>
          <p>
            {loading
              ? 'A carregar…'
              : filtrados.length === 1
                ? '1 espaço'
                : `${filtrados.length} espaços`}
          </p>
        </div>
        {erro ? <p className={styles.note}>{erro}</p> : null}
        {!loading && filtrados.length === 0 ? (
          <div className={styles.empty}>
            <strong>{q ? 'Nenhum espaço com esse nome.' : 'Ainda não há espaços publicados.'}</strong>
            <p>Se já tem o link da empresa, entre directamente com o identificador abaixo.</p>
          </div>
        ) : (
          <ul className={styles.grid}>
            {filtrados.map((espaco) => (
              <li key={espaco.slug}>
                <Link className={styles.card} to={getTenantEntryPath(espaco.slug)}>
                  {espaco.logoUrl ? (
                    <img src={resolveMediaUrl(espaco.logoUrl)} alt="" />
                  ) : (
                    <span className={styles.avatar} aria-hidden>
                      {espaco.nome.trim().slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span>
                    <strong>{espaco.nome}</strong>
                    <em>/e/{espaco.slug}</em>
                  </span>
                  <span className={styles.go}>Entrar</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <form className={styles.slugForm} onSubmit={abrirSlug}>
          <label htmlFor="slug">Já tem o link do espaço?</label>
          <div>
            <span>/e/</span>
            <input
              id="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="nome-da-empresa"
              autoCapitalize="none"
            />
            <button type="submit">Abrir</button>
          </div>
        </form>
      </section>

      <section id="como" className={styles.sectionAlt}>
        <h2>Como funciona para quem marca</h2>
        <ol className={styles.steps}>
          {STEPS.map((step) => (
            <li key={step.n}>
              <span>{step.n}</span>
              <strong>{step.title}</strong>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.section}>
        <h2>Feita com detalhe, não como um catálogo genérico</h2>
        <div className={styles.details}>
          {DETAILS.map((item) => (
            <article key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
        <div className={styles.ctaRow}>
          <a className={styles.primary} href={WA_ESPACO} target="_blank" rel="noopener noreferrer">
            Quero o meu espaço
          </a>
          <a className={styles.secondary} href={WA_APP} target="_blank" rel="noopener noreferrer">
            Quero a app
          </a>
        </div>
        <p className={styles.note}>
          Os botões abrem o WhatsApp da Agência Sol OnLine, onde a conversa continua.{' '}
          <a href={SOLONLINE_SITE_URL} target="_blank" rel="noopener noreferrer">
            solonline.pt
          </a>
        </p>
      </section>
    </div>
  )
}
